import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Card, Form, Breadcrumb, Row, Col, Spin, Image, Alert } from 'antd';
import { EyeOutlined, FileImageOutlined, DeleteOutlined, InfoCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { tenantService, Tenant } from '../services/tenants';
import TenantPreviewModal from '../components/TenantPreviewModal';
import { showConfirmModal } from '../components/PopupModal';
import authService from '../services/auth';
import { message } from '../utils/message';

const { Title, Text } = Typography;

const TenantIntroDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isEventContent, setIsEventContent] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  useEffect(() => {
    if (id) {
      loadTenantData();
    }
  }, [id]);

  const getLayoutDisplayName = (layout: string) => {
    switch (layout) {
      case 'layout1':
        return '레이아웃 1 (5개 구성)';
      case 'layout2':
        return '레이아웃 2 (4개 구성)';
      default:
        return '-';
    }
  };

  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '-';

    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '');

    if (numbers.length === 0) return '-';

    // 02로 시작하는 서울 지역번호
    if (numbers.startsWith('02')) {
      if (numbers.length === 9) {
        return numbers.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
      } else if (numbers.length === 10) {
        return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
      }
    }

    // 010, 011, 016, 017, 018, 019 휴대폰 번호
    if (/^01[016789]/.test(numbers)) {
      if (numbers.length === 10) {
        return numbers.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
      } else if (numbers.length === 11) {
        return numbers.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
      }
    }

    // 그 외 지역번호 (3자리)
    if (numbers.length === 10) {
      return numbers.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    } else if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }

    // 포맷팅할 수 없는 경우 원본 반환
    return phone;
  };

  const loadTenantData = async () => {
    try {
      setLoading(true);
      const response = await tenantService.getTenant(Number(id));
      if (response.success) {
        setTenant(response.data);
        // 이벤트 콘텐츠 여부 결정 (백엔드에서 받은 isEventContent 값 사용)
        const hasEventContent = response.data.isEventContent || false;
        setIsEventContent(hasEventContent);
        setApprovalStatus(response.data.approvalStatus || 'pending');
        setRejectionReason(response.data.rejectionReason || '');

        // 폼에 데이터 설정
        form.setFieldsValue({
          companyName: response.data.name,
          representative: formatPhoneNumber(response.data.phone || ''),
          content: response.data.description || '',
          eventBrand: hasEventContent ? 'Y' : 'N',
          exposure: response.data.status === 'active' ? '노출' : '비노출',
          layoutName: getLayoutDisplayName(response.data.layout),
          registrationDate: response.data.createdAt ? new Date(response.data.createdAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '2025.01.00',
          approvalDate: response.data.updatedAt ? new Date(response.data.updatedAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '2025.01.00',
          lastUpdateDate: response.data.updatedAt ? new Date(response.data.updatedAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').replace(/\.$/, '') : '2025.01.00'
        });
      }
    } catch (error) {
      console.error('Failed to load tenant data:', error);
      message.error('입주사 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    // 수정 페이지로 이동 (ID가 없으면 기본값 1 사용)
    const tenantId = id || '1';
    navigate(`/tenant-intro/edit/${tenantId}`);
  };

  const handleDelete = async () => {
    showConfirmModal({
      title: '알림',
      content: '삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?',
      okText: '확인',
      okType: 'danger',
      onConfirm: async () => {
        try {
          await tenantService.deleteTenant(Number(id));
          message.success('삭제가 완료되었습니다.');
          navigate('/tenant-intro');
        } catch (error) {
          console.error('Delete tenant error:', error);
          message.error('입주사 삭제에 실패했습니다.');
        }
      },
    });
  };

  const handlePreview = () => {
    if (!tenant) {
      message.warning('입주사 정보를 불러오는 중입니다.');
      return;
    }
    setPreviewModalVisible(true);
  };

  const renderApprovalAlert = () => {
    let alertMessage = '';
    let alertType: 'info' | 'warning' | 'error' = 'info';
    let icon = <InfoCircleOutlined />;

    const isSuperAdmin = authService.isSuperAdmin();

    switch (approvalStatus) {
      case 'pending':
        if (isSuperAdmin) {
          alertMessage = '승인이 필요한 콘텐츠입니다. 검토 후 승인 여부를 진행해주세요.';
          alertType = 'info';
        } else {
          alertMessage = '승인 대기중인 콘텐츠입니다. 수정 및 삭제가 불가합니다.';
          alertType = 'warning';
        }
        icon = <InfoCircleOutlined />;
        break;
      case 'approved':
        alertMessage = '승인 완료된 콘텐츠입니다.';
        alertType = 'info';
        icon = <InfoCircleOutlined />;
        break;
      case 'rejected':
        alertMessage = rejectionReason
          ? `승인 거부된 콘텐츠입니다.\n사유 : ${rejectionReason}`
          : '승인 거부된 콘텐츠입니다.';
        alertType = 'error';
        icon = <CloseCircleOutlined />;
        break;
    }

    return (
      <Alert
        message={alertMessage}
        type={alertType}
        icon={icon}
        showIcon
        style={{ whiteSpace: 'pre-line', marginBottom: '24px' }}
      />
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '건물 안내' },
          { title: '입주사 소개', href: '/tenant-intro' },
          { title: '입주사 소개 상세' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          입주사 소개 상세 {tenant?.name && `- ${tenant.name}`}
        </Title>
        <Button onClick={handlePreview}>미리보기</Button>
      </div>

      {renderApprovalAlert()}

      <Form
        form={form}
        layout="vertical"
        style={{ width: '100%' }}
      >
        {/* 기본 정보 */}
        <Card title="기본 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[12, 8]}>
            <Col span={6}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>레이아웃명</Text>
                <Text style={{ fontSize: '14px' }}>{form.getFieldValue('layoutName') || '-'}</Text>
              </div>
            </Col>
          </Row>
          <Row gutter={[12, 8]} style={{ paddingBottom: '0px'}}>
            <Col span={6}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>노출여부</Text>
                <Text style={{ fontSize: '14px' }}>{form.getFieldValue('exposure') || '-'}</Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 입주사 정보 */}
        <Card title="입주사 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[12, 8]}>
            <Col span={6}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>입주사명</Text>
                <Text style={{ fontSize: '14px' }}>{form.getFieldValue('companyName') || '-'}</Text>
              </div>
            </Col>
          </Row>
          <Row gutter={[12, 8]} style={{ paddingBottom: '0px'}}>
            <Col span={6}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>대표번호</Text>
                <Text style={{ fontSize: '14px' }}>{form.getFieldValue('representative') || '-'}</Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 입주사 설명 */}
        <Card title="입주사 설명" style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>내용</Text>
            <Text style={{ fontSize: '14px', whiteSpace: 'pre-wrap', display: 'block' }}>
              {form.getFieldValue('content') || '-'}
            </Text>
          </div>
        </Card>

        {/* 입주사 이미지 */}
        <Card title="입주사 이미지" style={{ marginBottom: '24px' }}>
          <div>
            <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '16px', color: '#ffffff' }}>
              첨부파일
            </Text>
            {tenant?.imageUrl ? (
              <div style={{ border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <Image
                    width={120}
                    height={90}
                    src={tenant.imageUrl}
                    alt="입주사 이미지"
                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                    preview={{
                      mask: <div><EyeOutlined /> 미리보기</div>
                    }}
                  />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <FileImageOutlined style={{ color: '#F24822' }} />
                      <Text style={{ color: '#ffffff' }}>입주사 대표 이미지</Text>
                    </div>
                    <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                      이미지가 등록되어 있습니다.
                    </Text>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                border: '1px dashed #303030',
                borderRadius: '8px',
                padding: '32px',
                textAlign: 'center',
                backgroundColor: '#1a1a1a'
              }}>
                <FileImageOutlined style={{ fontSize: '48px', color: '#666666', marginBottom: '16px' }} />
                <Text style={{ color: '#a6a6a6', display: 'block' }}>
                  등록된 이미지가 없습니다.
                </Text>
              </div>
            )}
          </div>
        </Card>

        {/* 기타 정보 */}
        <Card title="기타 정보" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>이벤트 콘텐츠</Text>
                <Text style={{ fontSize: '14px' }}>{form.getFieldValue('eventBrand') || '-'}</Text>
              </div>

              {/* 이벤트 콘텐츠가 Y일 때만 추가 첨부파일 표시 */}
              {isEventContent && (
                <div style={{ marginTop: '24px' }}>
                  <Text style={{ display: 'block', marginBottom: '16px', color: '#ffffff' }}>
                    이벤트 첨부파일
                  </Text>
                  {tenant?.thumbnailUrl ? (
                    <div style={{ border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <Image
                          width={120}
                          height={90}
                          src={tenant.thumbnailUrl}
                          alt="이벤트 이미지"
                          style={{ objectFit: 'cover', borderRadius: '4px' }}
                          preview={{
                            mask: <div><EyeOutlined /> 미리보기</div>
                          }}
                        />
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <FileImageOutlined style={{ color: '#F24822' }} />
                            <Text style={{ color: '#ffffff' }}>이벤트 이미지</Text>
                          </div>
                          <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                            이벤트 이미지가 등록되어 있습니다.
                          </Text>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      border: '1px dashed #303030',
                      borderRadius: '8px',
                      padding: '32px',
                      textAlign: 'center',
                      backgroundColor: '#1a1a1a'
                    }}>
                      <FileImageOutlined style={{ fontSize: '48px', color: '#666666', marginBottom: '16px' }} />
                      <Text style={{ color: '#a6a6a6', display: 'block' }}>
                        등록된 이벤트 이미지가 없습니다.
                      </Text>
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* 승인 정보 */}
        <Card title="승인 정보" style={{ marginBottom: '32px' }}>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>등록일</Text>
                <Text style={{ fontSize: '14px' }}>{form.getFieldValue('registrationDate') || '-'}</Text>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>승인일</Text>
                <Text style={{ fontSize: '14px' }}>{form.getFieldValue('approvalDate') || '-'}</Text>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#a6a6a6' }}>최근 수정일</Text>
                <Text style={{ fontSize: '14px' }}>{form.getFieldValue('lastUpdateDate') || '-'}</Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 액션 버튼 */}
        {tenant?.approvalStatus !== 'pending' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <Button
              danger
              size="large"
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              삭제
            </Button>
            {tenant?.approvalStatus === 'approved' && (
              <Button type="primary" size="large" onClick={handleEdit}>
                수정
              </Button>
            )}
          </div>
        )}
      </Form>

      <TenantPreviewModal
        visible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        tenant={tenant}
      />
    </Space>
  );
};

export default TenantIntroDetail;